import type { BillingMigrationUploadResponse } from '@store/generated/billing-migration';
import { enhancedApi as billingMigrationGeneratedApi } from '@store/generated/billing-migration';

/**
 * Billing Migration API - enhances generated endpoints with cache tags
 * and provides a custom upload mutation for multipart file handling.
 */
const enhancedBillingMigrationApi = billingMigrationGeneratedApi.enhanceEndpoints({
  addTagTypes: ['BillingMigration'],
  endpoints: {
    getV2ProviderBillingMigrationSessions: {
      providesTags: ['BillingMigration'],
    },
    getV2ProviderBillingMigrationSessionsBySessionId: {
      providesTags: (_result, _error, { sessionId }) => [
        { type: 'BillingMigration' as const, id: sessionId },
        'BillingMigration' as const,
      ],
    },
    postV2ProviderBillingMigrationSessionsBySessionIdConfirm: {
      invalidatesTags: ['BillingMigration'],
    },
    deleteV2ProviderBillingMigrationSessionsBySessionId: {
      invalidatesTags: ['BillingMigration'],
    },
  },
});

/**
 * Custom upload mutation - the generated code has void args for multipart uploads.
 * Injected into the enhanced API so it shares the BillingMigration tag type.
 */
export const billingMigrationApi = enhancedBillingMigrationApi.injectEndpoints({
  endpoints: builder => ({
    uploadBillingMigrationFiles: builder.mutation<
      BillingMigrationUploadResponse,
      { files: File[]; sourceSystem: string }
    >({
      query: ({ files, sourceSystem }) => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file, file.name));
        formData.append('source_system', sourceSystem);

        return {
          url: '/v2/provider/billing-migration/upload',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['BillingMigration'],
    }),
  }),
  overrideExisting: true,
});

// Friendly hook aliases
export const {
  useGetV2ProviderBillingMigrationSessionsQuery: useListBillingMigrationSessionsQuery,
  useGetV2ProviderBillingMigrationSessionsBySessionIdQuery: useGetBillingMigrationSessionQuery,
  usePostV2ProviderBillingMigrationSessionsBySessionIdConfirmMutation:
    useConfirmBillingMigrationMutation,
  useDeleteV2ProviderBillingMigrationSessionsBySessionIdMutation:
    useDeleteBillingMigrationSessionMutation,
  useUploadBillingMigrationFilesMutation,
} = billingMigrationApi;
